import React from 'react';
import PropTypes from 'prop-types';
import ASSIGNMENT_TYPES from '../../types/AssignmentTypes';
import mui from '../../utils/MuiIcons';
import UIHelper from '../../utils/UIHelper';

const ANIMATE_CLICK_EFFECT_DURATION_MS = 500;

/**
 * @typedef {import('../../types/Assignments').Assignment} Assignment
 */

/**
 * AssignmentsRow component - renders a single selectable row in the assignments table.
 * @param {Object} props
 * @param {Assignment} props.assignment - The assignment data object.
 * @param {boolean} props.isSelected - Whether this row is selected.
 * @param {function} props.onToggle - Callback when row selection is toggled.
 * @param {function} props.changeParentHandler - Callback to change the parent location in the URL.
 * @param {boolean} props.readOnly - Whether this assignment is read-only (already assigned to the sequence being edited).
 * @returns {JSX.Element}
 */
function AssignmentRow({ assignment, isSelected, onToggle, changeParentHandler, readOnly }) {
  /**
   * this state is to tract partial navigation in locations only
   * no functionality with devices
   */
  const [singleClickedLocationId, setSingleClickedLocationId] = React.useState(false);
  const isLocation = assignment.type === ASSIGNMENT_TYPES.location;
  const rowClassName = `type-${assignment.type}${singleClickedLocationId === assignment.id ? ' single-clicked' : ''}${isSelected ? ' is-selected' : ''}`;

  /*
   * this is to simulate double click in UX when clicking on location row
   * first click will mark it as single clicked and second click will navigate into it
   */
  React.useEffect(() => {
    if (!singleClickedLocationId) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setSingleClickedLocationId(false);
    }, ANIMATE_CLICK_EFFECT_DURATION_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [singleClickedLocationId]);

  /**
   * Handles the toggle click event to select/deselect the assignment.
   * @param {React.MouseEvent} e - The click event.
   */
  const toggleClick = e => {
    e.stopPropagation();
    onToggle(assignment);
  };

  /**
   * Gets the name block element for the assignment, including its name and location.
   * @returns {JSX.Element}
   */
  const getNameBlock = () => {
    if (assignment.type === ASSIGNMENT_TYPES.location) {
      return (
        <div>
          {assignment.parent}
          {UIHelper.asset.getAssetNameEle(assignment, 'assignment-table-row')}/
        </div>
      );
    }

    return (
      <>
        <div>{UIHelper.asset.getAssetNameEle(assignment, 'assignment-table-row')}</div>
        <div>Parent location: {assignment.parent}</div>
      </>
    );
  };

  /**
   * Gets the type icon element for the assignment based on its type.
   * @returns {JSX.Element}
   */
  const getType = () => {
    return assignment.type === ASSIGNMENT_TYPES.device ? (
      <span className={mui.notMuiClassName} title={assignment.type}>
        <img src="/assets/images/icons/devices_nav.png" alt={assignment.type} />
      </span>
    ) : (
      <span className={mui.className} title={assignment.type}>
        {mui.icons.location}
      </span>
    );
  };

  /**
   * Gets the description block element for the assignment, including its description and tags.
   * @returns {JSX.Element}
   */
  const getDescriptionBlock = () => {
    return (
      <>
        <div>{assignment.description}</div>
        <div>Tags: {assignment.tags?.join(', ')}</div>
      </>
    );
  };

  /**
   * Gets the action buttons element for the assignment row, showing add/remove based on selection state.
   * @returns {JSX.Element}
   */
  const getActionButtons = () => {
    if (readOnly) {
      return (
        <button
          type="button"
          className={`${mui.className} black-button`}
          disabled
          title="Device Already assigned to this Sequence"
          data-test="assignment-table-row-toggle-button"
        >
          {mui.icons.lock_outline}
        </button>
      );
    }

    return isSelected ? (
      <button
        type="button"
        className={`${mui.className} black-button`}
        onClick={toggleClick}
        data-test="assignment-table-row-toggle-button"
      >
        {mui.icons.remove}
      </button>
    ) : (
      <button
        type="button"
        className={`${mui.className} orange-button`}
        onClick={toggleClick}
        data-test="assignment-table-row-toggle-button"
      >
        {mui.icons.add}
      </button>
    );
  };

  /**
   * Handles the single click event on a location row.
   */
  const singleClickHandler = () => {
    if (singleClickedLocationId === assignment.id) {
      navigateToLocation();
      setSingleClickedLocationId(false);

      return;
    }

    setSingleClickedLocationId(assignment.id);
  };

  /**
   * Navigates into the location.
   */
  const navigateToLocation = () => {
    changeParentHandler(`${assignment.parent}${assignment.name}/`);
  };

  /**
   * Gets the populated block element showing the count of content and devices for the assignment.
   * @returns {string}
   */
  const getPopulatedBlock = () => {
    return isLocation ? `(${assignment.contentCount}/${assignment.deviceCount})` : '';
  };

  return (
    <tr
      className={rowClassName}
      onClick={isLocation ? singleClickHandler : undefined}
      data-id={assignment.id}
      data-type={assignment.type}
    >
      <td />
      <td>{getType()}</td>
      <td>{getNameBlock()}</td>
      <td>{getDescriptionBlock()}</td>
      <td>{getPopulatedBlock()}</td>
      <td>{getActionButtons()}</td>
    </tr>
  );
}

AssignmentRow.propTypes = {
  assignment: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  changeParentHandler: PropTypes.func.isRequired,
};

export default AssignmentRow;
